import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Images, Upload, Trash2, ArrowUp, ArrowDown, ExternalLink, X } from 'lucide-react';
import { toast } from 'sonner';
import ServiceImageEditor from './ServiceImageEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const MAX_MB = 6;

export default function SlideshowManager() {
  const { getAuthHeader, user } = useAuth();
  const [activePage, setActivePage] = useState('home'); // 'home' | 'services'
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user?.role === 'super_admin') fetchSlides();
    // Refetch whenever the active page tab changes.
  }, [user, activePage]);

  const fetchSlides = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/slideshow?page=${activePage}`, { headers: getAuthHeader() });
      setSlides(res.data || []);
    } catch {
      toast.error('Failed to load slides');
    } finally {
      setLoading(false);
    }
  };

  const onFileChosen = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please choose an image file (JPG, PNG, WEBP, GIF).');
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Image too large. Maximum size is ${MAX_MB} MB.`);
      return;
    }
    setSelectedFile(f);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadSlide = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please choose an image to upload');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('image', selectedFile);
      form.append('caption', caption.trim());
      form.append('page', activePage);
      await axios.post(`${API}/slideshow`, form, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Slide uploaded');
      setCaption('');
      clearSelection();
      fetchSlides();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload slide');
    } finally {
      setUploading(false);
    }
  };

  const deleteSlide = async (id) => {
    if (!window.confirm(`Remove this slide from the ${activePage === 'home' ? 'homepage' : 'Services page'}?`)) return;
    try {
      await axios.delete(`${API}/slideshow/${id}`, { headers: getAuthHeader() });
      toast.success('Slide removed');
      fetchSlides();
    } catch (err) {
      // Surface the real reason instead of a silent "Failed to delete".
      // A blanket catch was hiding legacy-data bugs (missing `id` field
      // on pre-migration slides → 404) from operators for weeks.
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Unknown error';
      const status = err?.response?.status;
      console.error('Slideshow delete failed:', { id, status, detail, err });
      toast.error(`Failed to delete: ${status ? status + ' — ' : ''}${detail}`);
    }
  };

  const move = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= slides.length) return;
    const ids = slides.map((s) => s.id);
    [ids[index], ids[newIndex]] = [ids[newIndex], ids[index]];
    try {
      await axios.patch(`${API}/slideshow/reorder`, { ids }, { headers: getAuthHeader() });
      fetchSlides();
    } catch {
      toast.error('Failed to reorder');
    }
  };

  if (user?.role !== 'super_admin') {
    return <div className="p-8 text-center"><p className="text-gray-600">Super Admin access required.</p></div>;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="slideshow-manager">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#193460] flex items-center gap-2">
          <Images className="w-7 h-7" /> Slideshow & Image Management
        </h2>
        <p className="text-gray-600 mt-1">
          Upload, reorder and remove the images that appear on the public site.
        </p>
      </div>

      {/* Page selector — Home vs Services */}
      <div
        role="tablist"
        aria-label="Slideshow page"
        className="inline-flex rounded-lg border-2 border-gray-200 bg-white p-1 mb-6"
        data-testid="slideshow-page-tabs"
      >
        {[
          { id: 'home', label: 'Home Page Images' },
          { id: 'services', label: 'Services Page Images' },
        ].map((p) => {
          const isActive = activePage === p.id;
          return (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActivePage(p.id)}
              data-testid={`slideshow-tab-${p.id}`}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-[#193460] text-white shadow'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Services tab renders the per-card image editor — the Services
          page on the public site has six fixed cards (one image each),
          not a generic ordered slideshow. */}
      {activePage === 'services' ? (
        <ServiceImageEditor />
      ) : (
      <>
      {/* Upload form */}
      <Card className="mb-6 border-2 border-gray-200">
        <CardContent className="p-6">
          <form onSubmit={uploadSlide} className="space-y-4">
            <div>
              <Label htmlFor="slide-file">Image file *</Label>
              <div className="mt-1 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#E87B6A] transition-colors">
                {preview ? (
                  <div className="space-y-3">
                    <img src={preview} alt="Preview" className="max-h-52 mx-auto rounded shadow" />
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                      <span className="truncate max-w-xs" data-testid="selected-filename">{selectedFile?.name}</span>
                      <button
                        type="button"
                        onClick={clearSelection}
                        className="text-red-600 hover:text-red-800 inline-flex items-center"
                        data-testid="clear-selection-btn"
                      >
                        <X className="w-4 h-4" /> Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <label htmlFor="slide-file" className="cursor-pointer block" data-testid="upload-dropzone">
                    <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-700 font-medium">Click to choose an image</p>
                    <p className="text-sm text-gray-500 mt-1">JPG, PNG, WEBP, GIF — up to {MAX_MB} MB</p>
                  </label>
                )}
                <input
                  id="slide-file"
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={onFileChosen}
                  className="hidden"
                  data-testid="slide-file-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="slide-caption">Caption (optional)</Label>
              <Input
                id="slide-caption"
                data-testid="slide-caption-input"
                placeholder="e.g., Volunteer Nigel fixing a radio"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={200}
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={uploading || !selectedFile} data-testid="upload-slide-btn"
                className="bg-[#E87B6A] hover:bg-[#d66a59] text-white">
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload Slide'}
              </Button>
            </div>
          </form>
          <p className="text-xs text-gray-500 mt-2">
            New slides appear at the end of the list. Use the arrow buttons below to reorder them.
          </p>
        </CardContent>
      </Card>

      {/* Current slides */}
      {loading ? (
        <p className="text-center text-gray-500 py-10">Loading slides...</p>
      ) : slides.length === 0 ? (
        <Card className="border-2 border-dashed border-gray-300">
          <CardContent className="p-10 text-center">
            <Images className="w-10 h-10 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600">
              No {activePage === 'home' ? 'Home Page' : 'Services Page'} images yet. Upload one above to start the slideshow.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="slides-grid">
          {slides.map((s, i) => (
            <Card key={s.id} className="overflow-hidden border-2 border-gray-200" data-testid={`slide-card-${i}`}>
              <div className="relative bg-gray-100 aspect-video">
                <img src={s.url} alt={s.caption || `Slide ${i + 1}`} className="w-full h-full object-cover" />
                <span className="absolute top-2 left-2 bg-[#193460] text-white text-xs font-bold px-2 py-1 rounded">
                  #{i + 1}
                </span>
              </div>
              <CardContent className="p-4 space-y-3">
                {s.caption ? (
                  <p className="text-sm text-gray-800 line-clamp-2">{s.caption}</p>
                ) : (
                  <p className="text-sm italic text-gray-400">No caption</p>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => move(i, -1)} disabled={i === 0}
                      data-testid={`slide-move-up-${i}`} title="Move up">
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => move(i, 1)} disabled={i === slides.length - 1}
                      data-testid={`slide-move-down-${i}`} title="Move down">
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    {s.url && !s.url.startsWith('data:') && (
                      <a href={s.url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center justify-center w-9 h-9 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                  <Button size="sm" onClick={() => deleteSlide(s.id)}
                    data-testid={`slide-delete-${i}`}
                    className="bg-red-600 hover:bg-red-700 text-white">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}
